import { useCallback, useMemo } from 'react';

import { buildFilterChips } from '../GeneratikTable.utils';
import { FilterFieldConfig, FilterValues } from '../GeneratikTableFilterPanel.types';
import { FilterChip } from '../GeneratikTableHeader.types';

interface UseGeneratikTableFiltersOptions {
  filterFields: FilterFieldConfig[];
  filterValues: FilterValues;
  setFilterValues: (values: FilterValues) => void;
}

interface UseGeneratikTableFiltersReturn {
  activeFilters: FilterChip[];
  handleFilterRemove: (filterId: string) => void;
  handleFilterApply: (values?: FilterValues) => void;
  handleFilterReset: () => void;
}

const useGeneratikTableFilters = ({
  filterFields,
  filterValues,
  setFilterValues,
}: UseGeneratikTableFiltersOptions): UseGeneratikTableFiltersReturn => {
  const activeFilters = useMemo(() => {
    if (Object.keys(filterValues).length > 0 && filterFields.length > 0) {
      return buildFilterChips(filterValues, filterFields);
    }
    return [];
  }, [filterValues, filterFields]);

  const handleFilterRemove = useCallback(
    (filterId: string) => {
      const newValues = { ...filterValues };
      delete newValues[filterId];
      setFilterValues(newValues);
    },
    [filterValues, setFilterValues],
  );

  const handleFilterApply = useCallback(
    (values?: FilterValues) => {
      if (values) {
        setFilterValues(values);
      }
    },
    [setFilterValues],
  );

  const handleFilterReset = useCallback(() => {
    setFilterValues({});
  }, [setFilterValues]);

  return {
    activeFilters,
    handleFilterRemove,
    handleFilterApply,
    handleFilterReset,
  };
};

export default useGeneratikTableFilters;

export type { UseGeneratikTableFiltersOptions, UseGeneratikTableFiltersReturn };
